import { mulberry32, pickWeighted } from "./rng";

// The Mail Bag (docs/design/HUB.md §Room 1): once per campaign day, shake
// the sack and pull one item. Mostly junk by design — the sock pile
// accumulating is the point. Rare pulls are flavor, special pulls are
// letters addressed to a living operator. Nothing here touches mechanics.

export type MailRarity = "common" | "uncommon" | "rare" | "special";

export type MailItem = {
  day: number;
  rarity: MailRarity;
  text: string;
};

export type MailState = {
  lastPullDay: number; // campaign day of the last pull (-1 = never pulled)
  log: MailItem[]; // newest first
};

export const MAIL_LOG_LIMIT = 20;

export function defaultMail(): MailState {
  return { lastPullDay: -1, log: [] };
}

// One pull per campaign day, and only once the campaign clock has started.
export function canPullMail(mail: MailState, campaignDay: number): boolean {
  return campaignDay > 0 && mail.lastPullDay < campaignDay;
}

const COMMON: readonly string[] = [
  "A single wool sock. No pair. The pile grows.",
  "A crayon drawing of a potato-shaped airplane. It's taped up anyway.",
  "A form letter from a bank in Omaha addressed to 'Dear Service Member'.",
  "Someone's aunt's fruitcake. It will survive the war.",
  "An expired copy of TIME. The world it describes no longer exists.",
  "A religious tract with a cartoon about honesty.",
  "Stale cookies. Eaten anyway. All of them.",
  "A plastic flag pin, made somewhere the flag isn't from.",
  "A knitted... hat? Scarf? It's ambiguous. It's warm.",
];

const UNCOMMON: readonly string[] = [
  "A tin of FRESH cookies. It sits on the ops table for exactly one day.",
  "A magazine from THIS month. Passed around until it dissolves.",
  "A polaroid of somebody's dog wearing sunglasses. Pinned to the board.",
  "A novelty mug: WORLD'S OKAYEST PILOT. It joins the collection.",
  "A postcard: GREETINGS FROM WALL DRUG! Nobody knows who sent it.",
];

const RARE: readonly string[] = [
  "Nose art stencils from a retired crew chief. The bird has a name now.",
  "A morale patch, unofficial and absolutely unauthorized. Everyone wants one.",
  "A cassette mixtape labeled ROAD TAPES VOL. III. The hub radio improves.",
  "A kid's drawing with real talent — a gunship over mountains, moon huge. Permanent bulletin-board spot.",
];

// Specials weave the roster in. {OP} is replaced with a living operator.
const SPECIAL: readonly string[] = [
  "A letter addressed to {OP} by name, in careful cursive. They read it twice and say nothing.",
  "From Tommy, age 7, Akron OH: 'Dear soldier, do you fly the plane with the big gun? I drew it. Write back.' {OP} writes back.",
  "A package for {OP}: photographs from home. They look older in the new ones.",
];

const RARITY_WEIGHTS: Array<{ value: MailRarity; weight: number }> = [
  { value: "common", weight: 60 },
  { value: "uncommon", weight: 25 },
  { value: "rare", weight: 10 },
  { value: "special", weight: 5 },
];

const POOLS: Record<MailRarity, readonly string[]> = {
  common: COMMON,
  uncommon: UNCOMMON,
  rare: RARE,
  special: SPECIAL,
};

// Pull one item. Seeded by the caller (flow passes a random seed — mail is
// a ritual, not a puzzle; it need not replay deterministically).
export function pullMail(
  mail: MailState,
  campaignDay: number,
  seed: number,
  livingOperators: readonly string[],
): MailState {
  if (!canPullMail(mail, campaignDay)) return mail;
  const rng = mulberry32(seed >>> 0);
  let rarity = RARITY_WEIGHTS[pickWeighted(rng, RARITY_WEIGHTS, (r) => r.weight)]!.value;
  // A letter needs someone alive to read it
  if (rarity === "special" && livingOperators.length === 0) rarity = "uncommon";
  const pool = POOLS[rarity];
  let text = pool[Math.floor(rng() * pool.length) % pool.length]!;
  if (rarity === "special") {
    const op = livingOperators[Math.floor(rng() * livingOperators.length) % livingOperators.length]!;
    text = text.replace("{OP}", op);
  }
  return {
    lastPullDay: campaignDay,
    log: [{ day: campaignDay, rarity, text }, ...mail.log].slice(0, MAIL_LOG_LIMIT),
  };
}

export function normalizeMail(value: unknown): MailState {
  if (typeof value !== "object" || value === null) return defaultMail();
  const v = value as Record<string, unknown>;
  const lastPullDay = typeof v.lastPullDay === "number" && Number.isFinite(v.lastPullDay)
    ? Math.floor(v.lastPullDay)
    : -1;
  const log: MailItem[] = [];
  if (Array.isArray(v.log)) {
    for (const item of v.log) {
      if (typeof item !== "object" || item === null) continue;
      const i = item as Record<string, unknown>;
      const rarity = i.rarity;
      if (rarity !== "common" && rarity !== "uncommon" && rarity !== "rare" && rarity !== "special") continue;
      if (typeof i.text !== "string" || i.text.length === 0 || i.text.length > 300) continue;
      const day = typeof i.day === "number" && Number.isFinite(i.day) ? Math.max(0, Math.floor(i.day)) : 0;
      log.push({ day, rarity, text: i.text });
      if (log.length >= MAIL_LOG_LIMIT) break;
    }
  }
  return { lastPullDay, log };
}
